import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { useEffect, useRef, useState } from 'react';
import useDADataTransformations, { DealRecieverDetailMap, MasterData } from './useDADataTransformations';
import {
    createDeal,
    getDealData,
    handleDealApplicationDrawer,
    resetDealFilters,
    setDealFilters,
    updateDeal,
    updateDealApplicationData,
} from '../../../store/investor/action';
import { getPlatformUserId, getSubdomain } from '../../../utils/utils';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { isEqual } from 'lodash';
import { toast } from 'react-toastify';
import { DEAL_CREATOR_TYPE } from '../../ManageDealDrawer/DealDrawerConstants';
import { Mixpanel } from '../../../utils/mixpanel';
import { DealFilters, defaultDealFilters } from '../../../store/investor/reducer';
import { AICA_SHARE_DEAL } from '../../../utils/constants/mixpanelEvents/investorEvents';
import { updateQueryParams } from '../../../utils/searchParamUtils';
import { DESIGNATION } from '../../../utils/constants/commonConstants';
import { slateToHtml } from '../../TextEditor/utils/SlateSerialisationUtil';

// A simple deep equality check.
function areObjectsEqual(objA: any, objB: any) {
    return isEqual(objA, objB);
}

const useFetchFormData = (enabled: boolean) => {
    const source = useAppSelector((state) => state.investor.dealApplicationDrawer.source);
    const drawerContent = useAppSelector((state) => state.investor.dealApplicationDrawer);
    const [formData, setFormData] = useState<MasterData | null>(null);
    const dealId = useAppSelector((state) => state.investor.dealApplicationDrawer.dealId);
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const [isFormValid, setIsFormValid] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const params = useParams();
    const dealFilters = useAppSelector((state) => state.investor.dealFilters);
    const [searchParams, setSearchParams] = useSearchParams();
    const [tempFilters, setTempFilters] = useState<DealFilters>(dealFilters);
    const { createDealData } = useDADataTransformations({
        source: source,
    });
    const dealProducts = useAppSelector((state) => state.investor.dealApplicationDrawer.allProducts);
    const user = useAppSelector((state) => state.user);
    const loggedInUser = user?.userData;
    const dispatch = useAppDispatch();
    const location = useLocation();
    const masterData = useAppSelector((state) => state.investor.dealApplicationDrawer.dealMasterData);
    const selectedStep = useAppSelector((state) => state.investor.dealApplicationDrawer.step);
    const [step, setStep] = useState(selectedStep ?? 0);
    // const [options, setOptions] = useState<{ label: string; value: string }[]>([]);
    const [orgMaps, setOrgMaps] = useState<Record<string, { label: string; value: string; userType?: string }[]>>({});

    // Use a ref to keep track of the last submitted form data.
    // This allows us to know whether the user's current formData differs from the last submitted version.
    const lastSubmittedDataRef = useRef<MasterData | null>(null);
    const dealTermsChanged = useRef<object | null>(null);
    const platformUserId = getPlatformUserId();
    // For multi-step navigation
    useEffect(() => {
        setTempFilters(dealFilters);
    }, [dealFilters]);

    useEffect(() => {
        if (selectedStep) {
            setStep(selectedStep);
        }
    }, [selectedStep]);

    const getFormData = () => {
        switch (source) {
            case 'debtProducts':
                return makeFormDataFromState();
            case 'createDeal':
            case 'editDeal':
                return fetchFormDataFromApi();
            default:
                break;
        }
    };

    const getTransformedData = (data: any, api: string, orgId: string) => {
        if (!data) return [];
        if (api === 'GET_ALL_PRODUCTS') {
            const transformedData = Object.values(data)?.map((item: any) => ({
                label: item.productName,
                value: item.productId,
            }));
            return transformedData;
        } else if (api === 'AICA_GET_ORG_USERS') {
            const mappedData = Object.values(data || {}).map((item: any) => ({
                label: item.name,
                value: item.userId,
                type: item.userType === 'LENDER' ? 'Admin' : 'Team Member',
            }));
            setOrgMaps({ ...orgMaps, [orgId]: mappedData.map(({ label, value, type }) => ({ label, value, type })) });

            return mappedData;
        } else {
            const transformedData = Object.values(data)?.map((item: any) => ({
                label: item.label,
                value: item._id,
            }));
            return transformedData;
        }
    };

    const fetchFormDataFromApi = () => {
        let payload: any = {};
        payload.dealId = dealId;
        dispatch(
            getDealData(payload, {
                onSuccess: (deal) => {
                    const masterData = createDealData(deal);
                    dispatch(
                        handleDealApplicationDrawer({
                            open: true,
                            source: source,
                            heading: drawerContent.heading,
                            subHeading: drawerContent.subHeading,
                            dealBeneficiaryId: drawerContent.dealBeneficiaryId,
                            dealId: deal.id,
                            width: '67%',
                        }),
                    );
                    setFormData(
                        masterData ?? {
                            products: [],
                            dealTerms: {},
                            dealReceiverDetailMap: new Map(),
                        },
                    );
                },
            }),
        );
    };

    const makeFormDataFromState = () => {
        // When loading the form data from state, set it into our local state.
        setFormData(
            masterData ?? {
                products: [],
                dealTerms: {},
                dealReceiverDetailMap: new Map(),
            },
        );
    };

    useEffect(() => {
        if (searchParams.get('dealCreated') !== 'true') {
            dealTermsChanged.current = formData?.dealTerms ?? null;
        }
    }, [step]);

    const updateDealTerms = (key: string, value: string | string[]) => {
        if (formData) {
            let updatedFormData: MasterData = {
                ...formData,
                dealTerms: {
                    ...formData.dealTerms,
                    [key]: value,
                },
            };
            if (key === 'loanTypes') {
                // Simply update products with the new value
                updatedFormData = {
                    ...updatedFormData,
                    products: Array.isArray(value) ? [...value] : [],
                };
            }

            setFormData(updatedFormData);
        }
    };

    const updateLenderMap = (lenderDetails: DealRecieverDetailMap) => {
        if (!formData) return;
        if (searchParams.get('dealCreated') === 'true') {
            searchParams.delete('dealCreated');
            setSearchParams(searchParams);
        }
        setFormData({
            ...formData,
            dealReceiverDetailMap: lenderDetails,
        });
    };

    useEffect(() => {
        if (formData) {
            const payload: any = {
                masterData: {
                    dealTerms: formData.dealTerms,
                    products: formData.products,
                    dealReceiverDetailMap: formData.dealReceiverDetailMap,
                },
            };

            if (drawerContent?.heading === 'Add Lender') {
                payload.step = 1;
            }

            dispatch(updateDealApplicationData(payload));
        }
    }, [formData]);

    const checkDiffOfDealTermsVSLenders = () => {
        // Check if formData.dealTerms is different from formData.dealReceiverDetailMap.dealTerms
        if (!formData) return false;
        for (const [key, value] of formData.dealReceiverDetailMap as DealRecieverDetailMap) {
            if (!areObjectsEqual(value.dealTerms, formData.dealTerms)) {
                return true;
            }
        }
        return false;
    };

    const getDealStatus = (step: number, isPrimaryClick: boolean, payload: any) => {
        if (source === 'editDeal') {
            if (step === 0) {
                return formData?.dealStatus;
            } else {
                const hasNewLenders =
                    Object.keys(payload.dealReceiverDetailMap).filter((key) => !payload.dealReceiverDetailMap[key].isExisting).length > 0;

                return hasNewLenders && isPrimaryClick ? 'SHARED_DEAL' : 'SHARED';
            }
        } else {
            return step === 1 && isPrimaryClick ? 'SHARED_DEAL' : formData?.dealStatus ?? 'DRAFT';
        }
    };

    const getDealRecieverMap = () => {
        if (!formData?.dealReceiverDetailMap) {
            return {};
        }
        // Convert the map into an array, process each entry, then build an object back
        const entries = Array.from(formData.dealReceiverDetailMap).reduce((acc: [string, any][], [key, value]: [string, any]): any => {
            // Skip existing entries if source is 'editTerms'
            if (source === 'editDeal' && value.isExisting) {
                return acc;
            }

            // Map loanType values to their labels
            const mappedLoanTypes = value.dealTerms?.loanTypes?.map((loanType: string) => {
                const matchedProduct = dealProducts?.find((product) => product.value === loanType);
                return matchedProduct?.label ?? loanType;
            });

            // Decide which dealTerms to use
            let dealTerms: any;
            if (searchParams.get('dealCreated') === 'true') {
                dealTerms = {
                    ...formData.dealTerms,
                    loanTypes: mappedLoanTypes,
                    dealNotes: undefined, // remove dealNotes
                };
            } else {
                dealTerms = {
                    ...value.dealTerms,
                    loanTypes: mappedLoanTypes,
                    dealNotes: undefined, // remove dealNotes
                };
            }
            if (dealTerms.hasOwnProperty('lenderUsers')) {
                const orgMapForKey = orgMaps[key];
                const mappedUsers =
                    orgMapForKey
                        ?.filter((user) => dealTerms.lenderUsers.includes(user.value))
                        .filter((user) => !user.userType || user.userType !== DESIGNATION.ADMIN) // By default All deals are sent to the Admin. Therefore, we filter out Admin users
                        .map((user) => ({
                            name: user.label,
                            userId: user.value,
                        })) || [];

                dealTerms.lenderUsers = {
                    userIds: mappedUsers,
                    type: mappedUsers.length > 0 ? 'SELECTED' : 'ALL',
                };
            } else {
                dealTerms.lenderUsers = { userIds: [], type: 'ALL' };
            }

            acc.push([
                key,
                {
                    dealTerms,
                    products: value.productEnums,
                },
            ]);

            return acc;
        }, []);

        // Build the final object from the processed entries
        return Object.fromEntries(entries);
    };

    const createDealPayload = (formData: MasterData, step: number, isPrimaryClick: boolean) => {
        let payload: any = {
            dealTerms: {
                ...formData.dealTerms,
                loanTypes: formData?.dealTerms?.['loanTypes']?.map((loanType: string) => {
                    return dealProducts?.find((product: { value: string; label: string }) => product.value === loanType)?.label;
                }),
            },
            products: formData.products,
            dealProposerId: platformData?.investorOrgId,
            dealBeneficiaryId: params.id ?? drawerContent.dealBeneficiaryId,
            dealCreatedBy: loggedInUser?.userName,
        };
        payload.dealReceiverDetailMap = getDealRecieverMap();

        payload.dealStatus = getDealStatus(step, isPrimaryClick, payload);

        if (dealId) payload.dealId = dealId;
        if (!dealId || step === 1) {
            payload.owner = {
                name: loggedInUser?.userName,
                userId: getPlatformUserId(),
                activityBy: loggedInUser?.userName,
                activityDate: new Date().toISOString(),
                ownerType: DEAL_CREATOR_TYPE,
            };
        }
        if (payload.dealTerms.hasOwnProperty('dealNotes')) {
            payload.dealTerms.dealNotes = [
                {
                    note: slateToHtml(payload.dealTerms.dealNotes),
                    noteBy: loggedInUser?.userName,
                    noteDate: new Date().toISOString(),
                },
            ];
        }
        payload.userId = platformUserId;
        return payload;
    };

    const handleStepClick = (futureStep: number, overrideCheck?: boolean) => {
        if (formData) {
            if (
                checkDiffOfDealTermsVSLenders() &&
                !overrideCheck &&
                step === 0 &&
                searchParams.get('dealCreated') !== 'true' &&
                !areObjectsEqual(formData.dealTerms, dealTermsChanged.current)
            ) {
                setShowConfirmation(true);
                return;
            }
            const payload = createDealPayload(formData, futureStep, false);

            // Combine cases 0 and 1 since they have identical code
            if (futureStep === 0 || futureStep === 1) {
                if (!areObjectsEqual(formData, lastSubmittedDataRef.current)) {
                    dispatch(
                        updateDeal(payload, {
                            onSuccess: () => {
                                setStep(futureStep);
                                lastSubmittedDataRef.current = formData;
                            },
                            onError: () => {
                                toast.error('Error updating deal');
                            },
                        }),
                    );
                } else setStep(futureStep);
            }
        }
    };
    const closeDrawer = () => {
        dispatch(
            handleDealApplicationDrawer({
                open: false,
                width: '0',
                source: '',
                dealId: '',
                heading: '',
                subHeading: '',
            }),
        );
    };
    const handleSecondaryClick = () => {
        if (source === 'DEAL_FILTERS') {
            dispatch(resetDealFilters());
            setTempFilters({ ...defaultDealFilters, statuses: [] });
            return;
        }
        if (step === 0) return;
        setStep((prev) => Math.max(prev - 1, 0));
    };
    const handlePrimaryClick = (overrideCheck = false) => {
        switch (source) {
            case 'DEAL_FILTERS':
                if (tempFilters) {
                    dispatch(setDealFilters(tempFilters));
                }
                closeDrawer();
                break;
            default:
                setLoading(true);
                if (formData) {
                    if (
                        checkDiffOfDealTermsVSLenders() &&
                        !overrideCheck &&
                        step === 0 &&
                        dealTermsChanged.current !== null &&
                        searchParams.get('dealCreated') !== 'true' &&
                        !areObjectsEqual(formData.dealTerms, dealTermsChanged.current)
                    ) {
                        setShowConfirmation(true);
                        setLoading(false);
                        return;
                    }
                    const payload = createDealPayload(formData, step, true);
                    if (source === 'debtProducts' || source === 'createDeal' || source === 'editDeal') {
                        // If this is the very first submission OR if the formData is different from the last submitted version,
                        // then we want to dispatch the API call.
                        if (step === 1 || !lastSubmittedDataRef.current || !areObjectsEqual(formData, lastSubmittedDataRef.current)) {
                            if (source === 'editDeal' || source === 'createDeal') {
                                updateQueryParams(searchParams, setSearchParams, { editDealUpdated: true }, location);
                            }
                            if (dealId) {
                                dispatch(
                                    updateDeal(payload, {
                                        onSuccess: () => {
                                            if (step === 1) {
                                                if (source === 'editDeal') {
                                                    Mixpanel.track(AICA_SHARE_DEAL, {
                                                        userRole: user?.userData?.accountType,
                                                        pnoPlatform: getSubdomain(),
                                                        pnoId: user?.userData?.investorOrgId,
                                                        userId: user?.userData?.userId,
                                                        currentUrl: window.location.href,
                                                        organizationId: drawerContent?.dealBeneficiaryId,
                                                        userEmail: user?.userData?.primaryEmail,
                                                        companyName: drawerContent?.subHeading,
                                                        // Fetch Names of All Lenders
                                                        lendersAdded: Object.keys(payload.dealReceiverDetailMap).map((key) => {
                                                            return payload.dealReceiverDetailMap[key].lenderName;
                                                        }),
                                                    });
                                                }
                                                dispatch(
                                                    handleDealApplicationDrawer({
                                                        open: false,
                                                        source: source,
                                                        dealId: '',
                                                        width: '',
                                                        heading: '',
                                                        subHeading: '',
                                                        showSuccessModal: true,
                                                        profileGenerationStatus: '',
                                                    }),
                                                );
                                                dispatch(
                                                    updateDealApplicationData({
                                                        masterData: {
                                                            products: [],
                                                            dealTerms: {},
                                                            dealReceiverDetailMap: new Map(),
                                                        },
                                                    }),
                                                );
                                            } else {
                                                setStep((prev) => prev + 1);
                                            }
                                        },
                                    }),
                                );

                                // Update the reference to the last submitted data.
                                lastSubmittedDataRef.current = formData;
                            } else {
                                dispatch(
                                    createDeal(payload, {
                                        onSuccess: (dealId) => {
                                            dispatch(
                                                handleDealApplicationDrawer({
                                                    open: true,
                                                    source: source,
                                                    heading: drawerContent.heading,
                                                    dealBeneficiaryId: drawerContent.dealBeneficiaryId,
                                                    subHeading: drawerContent.subHeading,
                                                    dealId: dealId,
                                                    width: '67%',
                                                }),
                                            );
                                            setStep((prev) => prev + 1);
                                            lastSubmittedDataRef.current = formData;
                                        },
                                    }),
                                );
                            }
                        } else {
                            setStep((prev) => prev + 1);
                        }
                    }
                }
                setLoading(false);
                break;
        }
    };

    useEffect(() => {
        if (!enabled) {
            setFormData({
                products: [],
                dealTerms: {},
                dealReceiverDetailMap: new Map(),
            });
            setStep(0);
            return;
        }
        if (source) {
            getFormData();
        }
    }, [source]);

    return {
        formData,
        step,
        setStep,
        updateDealTerms,
        handleBack: handleSecondaryClick,
        handlePrimaryClick,
        updateLenderMap,
        loading,
        handleStepClick,
        setIsFormValid,
        isFormValid,
        showConfirmation,
        setShowConfirmation,
        tempFilters,
        setTempFilters,
        dealProducts,
        getTransformedData,
        searchParams,
        setSearchParams,
    };
};

export default useFetchFormData;
